import { Grid } from "@mui/material";
import { useMemo } from "react";

import { useDashboardData, useDashboardWarningSettings } from "@/features/shared/hooks";
import { useWarningPageNavLabel } from "@/features/shared/hooks/use-warning-page-defaults";
import {
  findNavigationElementByKind,
  NAVIGATION_ELEMENT_KIND,
} from "@/lib/navigation-element-kinds";
import WarningsInteractiveMapPanel from "@/features/dashboard/components/warnings/warnings-interactive-map-panel";
import { useMaritimeAreasQuery } from "@/features/dashboard/components/warnings/maritime-areas.queries";
import {
  selectMaritimeWarnings,
  useMaritimeWarningsQuery,
} from "@/features/dashboard/components/warnings/maritime-warnings.queries";
import { buildMapRegions } from "@/features/dashboard/components/warnings/maritime-warnings.model";
import { ToolbarRoot, ToolbarTitle } from "@/features/dashboard/components/toolbar/toolbar";
import { MaritimeWarningTool } from "@/features/dashboard/components/warning-tool/maritime-warning-tool";
import { LandWarningTool } from "@/features/dashboard/components/warning-tool/land-warning-tool";
import toolbarStyles from "@/features/dashboard/components/toolbar/toolbar.module.css";
import Stack from "@/components/stack/stack";

/** Maritime warnings page: coastal and North Sea warnings on the interactive map. */
const WarningsOverviewPage = () => {
  const { enabledMaritimeDomains, maritimeWarningPage } = useDashboardWarningSettings();
  const { navigationElements } = useDashboardData();
  const navLabel = useWarningPageNavLabel();
  // Page title is the navigation label, editable in the pages overview.
  const title = navLabel(
    findNavigationElementByKind(
      navigationElements,
      NAVIGATION_ELEMENT_KIND.maritimeWarningsPage,
    ) ?? { type: NAVIGATION_ELEMENT_KIND.maritimeWarningsPage },
  );
  const showMaritimeMap = enabledMaritimeDomains.length > 0;

  const {
    data: areas = [],
    isLoading: areasLoading,
    isError: areasError,
  } = useMaritimeAreasQuery({ enabled: showMaritimeMap });
  const {
    data: warningsResponse,
    isLoading: warningsLoading,
    isError: warningsError,
  } = useMaritimeWarningsQuery({ enabled: showMaritimeMap });

  const warningsList = selectMaritimeWarnings(warningsResponse);

  const regions = useMemo(
    () => buildMapRegions(areas, warningsList, enabledMaritimeDomains),
    [areas, warningsList, enabledMaritimeDomains],
  );

  const lastPublishedAt = warningsResponse?.lastPublishedAt ?? null;

  return (
    <div>
      <ToolbarRoot>
        <ToolbarTitle title={title} />
        <Stack className={toolbarStyles.tools}>
          <Stack flexWrap="nowrap" alignItems="center" justifyContent="flex-end">
            <MaritimeWarningTool />
            <LandWarningTool />
          </Stack>
        </Stack>
      </ToolbarRoot>
      <Grid container spacing={2} paddingX={"20px"} paddingBottom={"20px"}>
        {showMaritimeMap && (
          <Grid item xs={12}>
            <WarningsInteractiveMapPanel
              regions={regions}
              enabledDomainIds={enabledMaritimeDomains}
              isLoading={areasLoading || warningsLoading}
              hasError={areasError || warningsError}
              lastPublishedAt={lastPublishedAt}
              title={maritimeWarningPage.title}
              description={maritimeWarningPage.description}
            />
          </Grid>
        )}
      </Grid>
    </div>
  );
};

export default WarningsOverviewPage;
